import React, { useEffect, useRef, useState } from "react";
import { Drawer, IconButton, TextField, MenuItem, Tooltip, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SelectComponent from "../../../../UniversalComponents/SelectComponent";
import { useDispatch } from "react-redux";
import { pushAlert } from "../../../../../utils/alertSlice";
import { addUpdateGroupMember, fetchManagePermissionGroup } from "../../../../../utils/api/managePermissionApi";
import LabelComponent from "../../../../UniversalComponents/LabelComponent";
import Button from "../../../../UniversalComponents/DefaultButton";

const trackingViews = [
  { label: "CRM View 1", value: "CRM View 1" },
  { label: "CRM View 2", value: "CRM View 1" },
  { label: "CRM View 3", value: "CRM View 1" },
];

const allPermissions = [
  "Documents",
  "Services",
  "Billing",
  "Reporting Team",
  "Customer Delegation",
  "Add & Manage Fields",
  "Download",
  "Status Update",
];

const AddGroupDrawer = ({ onClose, setRefetch }) => {
  const [form, setForm] = useState({
    groupName: "",
    description: "",
    trackingView: "",
    permissions: {},
  });

  const dispatch = useDispatch();

  const [viewsList, setViewsList] = useState([]);
  const [permissionList, setPermissionList] = useState([]);
  const drawerRef = useRef(null);

  useEffect(() => {
    drawerRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);
  const handleChange = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
  };

  const handlePermissionToggle = (perm) => {
    setForm((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [perm?.value]: !prev.permissions[perm?.value],
      },
    }));
  };

  const getPermissionList = async () => {
    try {
      const response = await fetchManagePermissionGroup({ query: { key: "all_permission" } });
      if (response?.success) {
        setPermissionList(
          response?.data?.map((data) => {
            return { label: data?.name, value: data?.id };
          })
        );
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    }
  };

  const getViewsList = async () => {
    try {
      const response = await fetchManagePermissionGroup({ query: { key: "all_views" } });
      if (response?.success) {
        setViewsList(
          response?.data?.map((data) => {
            return {
              label: data?.view_name,
              value: data?.id,
            };
          })
        );
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message, type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message, type: "danger" }));
    }
  };

  const handleAddGroup = async (form) => {
    try {
      // Basic required field checks
      if (!form?.groupName?.trim() || !form?.description?.trim()) {
        dispatch(pushAlert({ visible: true, message: "Group Name and Description are required.", type: "danger" }));
        return;
      }

      if (!form?.trackingView || form.trackingView.length === 0) {
        dispatch(pushAlert({ visible: true, message: "At least one view must be selected.", type: "danger" }));
        return;
      }

      if (!form?.permissions || Object.keys(form.permissions).length === 0) {
        dispatch(pushAlert({ visible: true, message: "At least one permission must be selected.", type: "danger" }));
        return;
      }

      const groupData = {
        group_name: form.groupName,
        group_description: form.description,
        views: form.trackingView,
        modules: Object.keys(form.permissions).map((permissionId) => ({
          module_id: permissionId,
          module_permission: form.permissions[permissionId],
        })),
      };

      const response = await addUpdateGroupMember({ body: groupData });

      if (response?.success) {
        dispatch(pushAlert({ visible: true, message: response.message, type: "success" }));
        setRefetch((prev) => !prev);
        onClose();
      } else {
        dispatch(pushAlert({ visible: true, message: response?.message || "Failed to add group.", type: "danger" }));
      }
    } catch (error) {
      dispatch(pushAlert({ visible: true, message: error?.message || "Unexpected error occurred.", type: "danger" }));
    }
  };

  useEffect(() => {
    getPermissionList();
    getViewsList();
  }, []);

  return (
    <div className="h-full border-[1px] border-gray-200" onClose={onClose}>
      <div className="p-4 flex flex-col h-full justify-between gap-4">
        {/* Header */}
        <div className="flex flex-col min-h-0 grow gap-4">
          <div className="flex justify-between items-center" ref={drawerRef}>
            <h2 className="text-[16px] font-medium text-[#1E293B]">Add Group</h2>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </div>

          <div className="flex flex-col gap-4 overflow-auto">
            {/* Group Name */}
            <div>
              <LabelComponent labelText="Group Name" isMandatory={true} />
              <TextField
                fullWidth
                size="small"
                placeholder="Enter Group Name"
                value={form.groupName}
                onChange={handleChange("groupName")}
              />
            </div>

            {/* Description */}
            <div>
              <LabelComponent labelText="Description" isMandatory={true} />
              <TextField
                fullWidth
                size="small"
                placeholder="Select Tracking View"
                value={form.description}
                onChange={handleChange("description")}
              />
            </div>

            {/* Tracking View */}
            <div>
              <LabelComponent labelText="Tracking View (CRM)" isMandatory={true} />
              <SelectComponent
                label="Select Tracking View"
                options={viewsList || []}
                value={form?.trackingView?.label}
                onChange={handleChange("trackingView")}
                multiSelect
              />
            </div>

            {/* All Permissions */}
            <div className="flex items-center">
              <span className="text-sm font-medium text-gray-700">All Permissions</span>
              <Tooltip title="Assign permissions to this group">
                <InfoOutlinedIcon className="ml-1 text-gray-400" fontSize="small" />
              </Tooltip>
            </div>

            <div className="flex flex-col gap-2">
              {permissionList.map((perm, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between bg-[#F9FAFB] border border-gray-200 rounded-md px-3 py-2"
                >
                  <span className="text-sm text-gray-800">{perm?.label}</span>
                  <Switch
                    size="small"
                    checked={!!form.permissions[perm?.value]}
                    onChange={() => handlePermissionToggle(perm)}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Buttons (optional) */}
        <div className="flex justify-end gap-3">
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              handleAddGroup(form);
            }}
          >
            Create
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AddGroupDrawer;
